/* ============================================================================
   Lab Effects — Plant Source Block
   PDP configure block: Plant Source cards + Size chips + summary + Add to cart,
   plus the CDT / CBS / BDT tag that sits on the product image.

   Zero dependencies, no build step. Usage:

     const block = LEPlantSource.mount(document.getElementById('mount'), DATA, {
       tagEl: document.querySelector('[data-ps-tag]'),   // optional image tag
       onChange(sel) { ... },                            // fires on every change
       onAddToCart(sel) { ... }                          // return a Promise, optional
     });

   DATA shape is at the bottom of this file (LEPlantSource.SAMPLE). In production
   that object is what Shopify fills in. See SHOPIFY-NOTES.md.
   ========================================================================== */
(function (global) {
  "use strict";

  /* ---------- inline icons (monoline, currentColor) ---------- */
  const ICON_CHECK = '<svg viewBox="0 0 24 24"><path d="M4 12.5l6 6L20 6" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  const ICON_INFO = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><circle cx="12" cy="12" r="9"/><path d="M12 11v5.5" stroke-linecap="round"/><circle cx="12" cy="7.6" r="1.2" fill="currentColor" stroke="none"/></svg>';
  const ICON_CART = '<svg aria-hidden="true" viewBox="0 0 20 20" fill="none"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M16.608 9.421V6.906H3.392v8.016c0 .567.224 1.112.624 1.513.4.402.941.627 1.506.627H8.63M8.818 3h2.333c.618 0 1.212.247 1.649.686a2.35 2.35 0 0 1 .683 1.658v1.562H6.486V5.344c0-.622.246-1.218.683-1.658A2.33 2.33 0 0 1 8.82 3"></path><path stroke="currentColor" stroke-linecap="round" stroke-width="1.5" d="M14.608 12.563v5m2.5-2.5h-5"></path></svg>';

  const esc = (s) => String(s == null ? "" : s).replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
  const money = (cents, cur) => {
    const n = cents / 100;
    return (cur || "$") + (n % 1 === 0 ? n.toLocaleString("en-US") : n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
  };

  function LEPlantSourceBlock(root, data, opts) {
    this.root = root;
    this.data = data;
    this.opts = opts || {};
    this.state = { src: data.defaultSourceKey || data.sources[0].key, size: data.defaultSizeMl || data.sizes[0].ml, qty: 1 };
    this.root.classList.add("le-ps");
    this._bind();
    this.render();
  }

  LEPlantSourceBlock.prototype = {

    /* ---------- lookups ---------- */
    source() { return this.data.sources.find((s) => s.key === this.state.src) || this.data.sources[0]; },
    sizeObj(ml) { return this.data.sizes.find((z) => z.ml === (ml == null ? this.state.size : ml)); },
    /* A variant is one (source x size) pair. This is the single Shopify join point. */
    variant(sourceKey, ml) { return this.data.variants[sourceKey + ":" + ml] || null; },
    /* Current full selection, handed to onChange / onAddToCart / the change event. */
    selection() {
      const s = this.source(), v = this.variant(s.key, this.state.size);
      return {
        sourceKey: s.key, sourceAcronym: s.acr, sourceName: s.name,
        sizeMl: this.state.size, quantity: this.state.qty,
        variantId: v ? v.id : null, available: !!(v && v.available !== false),
        price: v ? v.price : null, compareAtPrice: v ? v.compareAtPrice : null,
        sku: v ? v.sku : null
      };
    },

    /* ---------- markup ---------- */
    _vars(s) { return "--a:" + s.accent + ";--deep:" + s.deep + ";--tint:" + s.tint; },
    _perMl(v, ml) { return (v.price / 100 / ml).toFixed(2); },

    _card(s) {
      const on = s.key === this.state.src, ml = this.state.size, z = this.sizeObj(), v = this.variant(s.key, ml);
      const cur = this.data.currencySymbol;
      const off = v && v.compareAtPrice ? Math.round((1 - v.price / v.compareAtPrice) * 100) : 0;
      const price = v
        ? '<span class="le-ps__mlpill">' + esc(z.label) + 'ml</span>' +
          '<span class="le-ps__prow"><strong>' + money(v.price, cur) + '</strong>' +
          '<span class="le-ps__perml"><b>' + cur + this._perMl(v, ml) + '</b> per ml</span></span>' +
          (off ? '<span class="le-ps__was"><s>' + money(v.compareAtPrice, cur) + '</s><em class="le-ps__off">' + off + '% OFF</em></span>' : "")
        : '<span class="le-ps__perml">Not available in ' + esc(z.label) + 'ml</span>';
      return '<div class="le-ps__card' + (on ? " is-on" : "") + '" style="' + this._vars(s) + '" data-src="' + esc(s.key) + '" role="button" tabindex="0" aria-pressed="' + on + '">' +
        '<span class="le-ps__tick">' + ICON_CHECK + '</span>' +
        '<div class="le-ps__card-body">' +
          (s.callout ? '<span class="le-ps__callout">' + esc(s.callout) + '</span>' : "") +
          '<h4>' + esc(s.name) + '</h4><p>' + esc(s.description) + '</p>' +
          '<div class="le-ps__price">' + price + '</div>' +
        '</div>' +
        '<div class="le-ps__card-foot"><span class="le-ps__left">' +
          '<img class="le-ps__badge" src="' + esc(s.icon) + '" alt=""><span class="le-ps__acr">' + esc(s.acr) + '</span></span>' +
          '<span class="le-ps__about" data-about="' + esc(s.key) + '" role="button" tabindex="0">' + ICON_INFO + 'About</span>' +
        '</div></div>';
    },

    _chips() {
      const s = this.source();
      return '<div class="le-ps__chips">' + this.data.sizes.map((z) => {
        const on = z.ml === this.state.size, v = this.variant(s.key, z.ml);
        return '<button type="button" class="le-ps__chip' + (on ? " is-on" : "") + '" style="' + this._vars(s) + '" data-size="' + z.ml + '"' +
          (v ? "" : " disabled") + ' aria-pressed="' + on + '"><b>' + esc(z.label) + 'ml</b>' + (z.note ? "<span>" + esc(z.note) + "</span>" : "") + "</button>";
      }).join("") + "</div>";
    },

    render() {
      const s = this.source(), ml = this.state.size, z = this.sizeObj(), v = this.variant(s.key, ml), cur = this.data.currencySymbol;
      const off = v && v.compareAtPrice ? Math.round((1 - v.price / v.compareAtPrice) * 100) : 0;
      this.root.setAttribute("style", this._vars(s));
      this.root.innerHTML =
        '<div class="le-ps__sec"><div class="le-ps__hd"><h3>' + esc(this.data.sourceLabel || "Plant Source") + "</h3></div>" +
          '<div class="le-ps__cards">' + this.data.sources.map((o) => this._card(o)).join("") + "</div></div>" +
        '<div class="le-ps__sec le-ps__sec--size"><div class="le-ps__hd"><h3>' + esc(this.data.sizeLabel || "Size") + "</h3></div>" + this._chips() + "</div>" +
        '<div class="le-ps__sum"><span class="le-ps__sum-l"><img class="le-ps__badge" src="' + esc(s.icon) + '" alt="">' +
          "<span><strong>" + esc(this.data.productTitle) + "</strong><i>" + esc(s.name) + "</i></span></span>" +
          '<span class="le-ps__sum-r"><span class="le-ps__sum-top"><em class="le-ps__mlpill">' + esc(z.label) + "ml</em><strong>" +
          (v ? money(v.price, cur) : "&mdash;") + "</strong></span>" +
          (v ? "<i>" + cur + this._perMl(v, ml) + " per ml" + (off ? " / saving " + off + "%" : "") + "</i>" : "<i>Unavailable</i>") + "</span></div>" +
        '<div class="le-ps__buy"><span class="le-ps__qty"><button type="button" data-qty="-1" aria-label="Decrease quantity">\u2212</button><b>' + this.state.qty +
          '</b><button type="button" data-qty="1" aria-label="Increase quantity">+</button></span>' +
          '<button type="button" class="le-ps__cart"' + (v && v.available !== false ? "" : " disabled") + ">" + ICON_CART +
          (v && v.available !== false ? "Add to cart" : "Sold out") + "</button></div>";
      this._paintTag(s);
      const sel = this.selection();
      if (this.opts.onChange) this.opts.onChange(sel, this);
      this.root.dispatchEvent(new CustomEvent("plantsource:change", { bubbles: true, detail: sel }));
    },

    /* the CDT / CBS / BDT tag that overlays the product image */
    _paintTag(s) {
      const el = this.opts.tagEl;
      if (!el) return;
      el.classList.add("le-ps-tag");
      el.innerHTML = '<img src="' + esc(s.icon) + '" alt=""><span>' + esc(s.acr) + "</span>";
      el.setAttribute("title", s.name);
    },

    /* ---------- About sheet ---------- */
    openAbout(key) {
      const s = this.data.sources.find((o) => o.key === key);
      if (!s || !s.about) return;
      let ovl = document.querySelector(".le-ps-ovl");
      if (!ovl) { ovl = document.createElement("div"); ovl.className = "le-ps-ovl"; document.body.appendChild(ovl); }
      this._ovl = ovl;
      ovl.innerHTML = '<div class="le-ps-sheet" style="' + this._vars(s) + '" role="dialog" aria-modal="true" aria-label="About ' + esc(s.name) + '">' +
        '<div class="le-ps-sheet__head"><img src="' + esc(s.icon) + '" alt=""><div><h3>' + esc(s.name) + "</h3>" +
        '<span class="k" style="color:' + s.accent + '">' + esc(s.acr) + (s.complianceShort ? " \u00b7 " + esc(s.complianceShort) : "") + "</span></div>" +
        '<button type="button" class="le-ps-sheet__x" data-ps-close aria-label="Close">\u2715</button></div>' +
        '<div class="le-ps-sheet__body"><dl>' +
        (s.about.what ? "<dt>What it is</dt><dd>" + esc(s.about.what) + "</dd>" : "") +
        (s.about.how ? "<dt>How we make it</dt><dd>" + esc(s.about.how) + "</dd>" : "") +
        (s.about.who ? "<dt>Best for</dt><dd>" + esc(s.about.who) + "</dd>" : "") +
        (s.about.rule ? "<dt>Compliance</dt><dd>" + esc(s.about.rule) + "</dd>" : "") +
        '</dl><div class="le-ps-sheet__foot"><button type="button" class="le-ps-sheet__pick" data-ps-pick="' + esc(s.key) + '">Choose ' + esc(s.acr) + "</button>" +
        '<button type="button" class="le-ps-sheet__close" data-ps-close>Close</button></div></div></div>';
      ovl.classList.add("is-open");
      if (!ovl.__wired) {
        ovl.__wired = true;
        ovl.addEventListener("click", (e) => {
          const pick = e.target.closest("[data-ps-pick]");
          if (pick) { this.select(pick.dataset.psPick); this.closeAbout(); return; }
          if (e.target === ovl || e.target.closest("[data-ps-close]")) this.closeAbout();
        });
      }
    },
    closeAbout() { if (this._ovl) this._ovl.classList.remove("is-open"); },

    /* ---------- public setters ---------- */
    select(key) { if (this.state.src !== key) { this.state.src = key; this.render(); } },
    setSize(ml) { if (this.state.size !== ml) { this.state.size = ml; this.render(); } },
    setQty(n) { this.state.qty = Math.max(1, n | 0); this.render(); },

    addToCart() {
      const sel = this.selection();
      if (!sel.available) return;
      if (this.opts.onAddToCart) return this.opts.onAddToCart(sel, this);
      /* default Shopify AJAX cart call, harmless on the dummy page (it just fails softly) */
      return fetch("/cart/add.js", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items: [{ id: sel.variantId, quantity: sel.quantity }] })
      }).then((r) => r.json());
    },

    /* ---------- events (delegated once on the root) ---------- */
    _bind() {
      this.root.addEventListener("click", (e) => {
        const about = e.target.closest("[data-about]");
        if (about) { e.preventDefault(); e.stopPropagation(); this.openAbout(about.dataset.about); return; }
        const src = e.target.closest("[data-src]");
        if (src) { this.select(src.dataset.src); return; }
        const size = e.target.closest("[data-size]");
        if (size) { this.setSize(+size.dataset.size); return; }
        const qty = e.target.closest("[data-qty]");
        if (qty) { this.setQty(this.state.qty + +qty.dataset.qty); return; }
        if (e.target.closest(".le-ps__cart")) this.addToCart();
      });
      this.root.addEventListener("keydown", (e) => {
        if (e.key !== "Enter" && e.key !== " ") return;
        const t = e.target;
        if (t.matches("[data-about]")) { e.preventDefault(); this.openAbout(t.dataset.about); }
        else if (t.matches("[data-src]")) { e.preventDefault(); this.select(t.dataset.src); }
      });
      document.addEventListener("keydown", (e) => { if (e.key === "Escape") this.closeAbout(); });
    }
  };

  global.LEPlantSource = {
    mount(root, data, opts) { return new LEPlantSourceBlock(root, data, opts); },
    Block: LEPlantSourceBlock
  };
})(window);
